import {Back, TimelineLite} from "gsap";
import * as HLF from "html-living-framework";
import {PixiUtils} from "html-living-framework";
import {AppProgress} from "../Data/AppProgress";
import {SPRITESHEET_PUPPET_PREFIX} from "../Data/Constants";
import {GameType} from "../Data/GameData";
import * as Topics from "../Data/Topics";
import {PuppetGameButton} from "../GameObjects/PuppetGameButton";
import {PuppetScreen} from "../GameObjects/PuppetScreen";
import {PuppetType} from "../GameObjects/PuppetType";
import {PuppetTypeButton} from "../GameObjects/PuppetTypeButton";
import {main} from "../Layout/PuppetLayout";
import {array} from "../Utils/array";
import {Container} from "../Utils/Container";
import {Sprite} from "../Utils/Sprite";
import {Game} from "./Game";

export class GamePuppet extends Game {

    public readonly main = this.addChild(new Container());
    public readonly vignette = this.main.addChild(new Sprite());
    public readonly screen = this.main.addChild(new PuppetScreen());
    public readonly buttonContainer = this.main.addChild(new Container());
    public readonly buttonSubmitPos = {x: 0, y: 0};
    public readonly buttons = array((i: PuppetType) => this.buttonContainer.addChild(new PuppetTypeButton(i)));
    public readonly buttonLight = this.buttonContainer.addChild(new PuppetGameButton());
    public readonly allButtons: PuppetGameButton[] = [];

    protected readonly _gameType = GameType.PUPPET;
    private _isPlayingFirstFact = false;
    private _isButtonsOnboarding = false;
    private _isLightOn = false;

    public static getRequiredAssets(): string[] {
        return [
            ...super.getRequiredAssets(),
            ...[1].map((i) => SPRITESHEET_PUPPET_PREFIX + i + HLF.ResourceUtils.FILE_EXTENSION_JSON),
            ...[1, 2].map((i) => SPRITESHEET_PUPPET_PREFIX + "shared" + i + HLF.ResourceUtils.FILE_EXTENSION_JSON),
        ];
    }

    public init(pSize: PIXI.Point): void {

        this.addChildAt(this.main, 1);
        main.call(this);
        this.main.scale.set(0.5);

        this._funFactCommon = ["andy_09", "andy_10", "andy_11"];
        this._funFactUnique = [[]];

        super.init(pSize);

        this.vignette.alpha = 0;
        this._buttonClear.visible = false;
        PixiUtils.sendToFront(this._buttonSubmit);

        this.buttons.forEach((button) => {
            button.setCallback(() => {
                this.screen.incrementItem(button.puppetType);
                PubSub.publish(
                    button.puppetType === 0 ? Topics.ONBOARDING_START : Topics.HIEROGLYPH_CHANGED,
                    button.puppetType);
            });
        });
        this.buttonLight.setLayoutTexture(`button-light-off`, 0.5, 0.5);
        this.buttonLight.setCallback(() => {
            this.toggleLight();
            PubSub.publish(Topics.HIEROGLYPH_CHANGED, this.buttons.length);
        });
        this.allButtons.push(...this.buttons, this.buttonLight);

        const data = AppProgress.games[this._gameType].saveData[0];
        if (data) {
            this.screen.load(data);
        }
    }

    public update(pDeltaTime: number): void {
        super.update(pDeltaTime);
    }

    public animateIn(pOnComplete: () => void): void {
        super.animateIn(pOnComplete);

        this._buttonSubmit.visible = false;
        this.allButtons.forEach((it) => it.interactive = false);

        new TimelineLite()
            .from(this.screen, 1, {
                y: -this._size.y - this.screen.height * 0.5,
                ease: Back.easeOut,
            }, 1)
            .staggerFrom(this.allButtons, 0.4, {
                y: this._size.y + this.allButtons[0].height * 0.5,
                ease: Back.easeOut,
            }, 0.1)
            .call(() => this.onAnimateInComplete());
    }

    public animateOut(pOnComplete: () => void): void {
        super.animateOut(pOnComplete);
    }

    public onResize(pSize: PIXI.Point): void {
        super.onResize(pSize);
    }

    protected onAnimateInComplete(): void {
        super.onAnimateInComplete();

        if (this._isOnboarding) {
            this.startOnboarding();
        } else {
            this.allButtons.forEach((it) => it.interactive = true);
            PubSub.publishSync(HLF.REGISTER_FOCUSABLES, this.allButtons);
            this.showButtonSubmit();
        }
    }

    protected saveData(): void {
        AppProgress.games[this._gameType].saveData[0] = this.screen.save();
        AppProgress.games[this._gameType].recentItemIndex = 0;
    }

    protected getAndyPos(): PIXI.Point {
        return new PIXI.Point(
            Math.max(this._size.x * 0.5 - this._andy.width * 0.5 - 50, 425),
            this._size.y * 0.5 + this._andy.height * 0.3,
        );
    }

    protected getSubmitButtonPos(): PIXI.Point {
        return new PIXI.Point(
            Math.max(this.buttonSubmitPos.x / 2, this._size.x * 0.5 - 130),
            this.buttonSubmitPos.y / 2,
        );
    }

    protected playFunFact(cb?: () => void) {
        if (!this._isPlayingFirstFact) {
            super.playFunFact(cb);
        }
    }

    private showButtonSubmit() {
        if (this._buttonSubmit.visible || this._isPlayingFirstFact || this._isButtonsOnboarding) {
            return;
        }
        this._buttonSubmit.visible = true;
        this._buttonSubmit.interactive = false;
        new TimelineLite()
            .from(this._buttonSubmit, 0.4, {
                x: this._size.x + this._buttonSubmit.width * 0.5,
                ease: Back.easeOut,
            })
            .call(() => {
                this._buttonSubmit.interactive = true;
                PubSub.publishSync(HLF.REGISTER_FOCUSABLES, [this._buttonSubmit]);
            });
    }

    private startOnboarding(): void {
        this._onboardingManager.clear();
        this.allButtons.forEach((button, i) => {
            const isFirstStep = i === 0;
            const isLastStep = i === this.allButtons.length - 1;
            const step = this._onboardingManager.addStep(
                isFirstStep ? Topics.ONBOARDING_START : Topics.HIEROGLYPH_CHANGED, i, button,
                isFirstStep ? 2 : 0.5, true, undefined,
                () => {
                    this._isButtonsOnboarding = true;
                    button.interactive = true;
                    PubSub.publishSync(HLF.REGISTER_FOCUSABLES, [button]);
                    if (isFirstStep) {
                        this.screen.newTimeline().call(() => {
                            this.playFunFact(() => {
                                this._isPlayingFirstFact = false;
                                this.showButtonSubmit();
                            });
                            this._isPlayingFirstFact = true;
                        }, undefined, undefined, 2);
                    }
                },
                isLastStep ? () => {
                    this._isButtonsOnboarding = false;
                    this.showButtonSubmit();
                } : undefined);
            step.skipAndyReaction = !isFirstStep;
        });
        this._onboardingManager.start();
    }

    private toggleLight() {
        this._isLightOn = !this._isLightOn;
        this.buttonLight.interactive = false;
        this.buttonLight.setLayoutTexture(`button-light-${this._isLightOn ? "on" : "off"}`, 0.5, 0.5);
        PubSub.publishSync(HLF.PLAY_AUDIO,
            new HLF.AudioToken(`light-${this._isLightOn ? "on" : "off"}`, 1, false, HLF.AudioCategory.SFX));
        const timeline = this.vignette.newTimeline();
        if (this._isLightOn) {
            this.screen.startMove();
            this.screen.flickerLight();
            this.vignette.alpha = 0;
            timeline.to(this.vignette, 0.3, {alpha: 1}, 0);
            timeline.to(this.screen.itemsContainer, 0.3, {alpha: 0.6}, 0);
        } else {
            this.screen.stopMove();
            this.screen.stopFlickerLight();
            timeline.to(this.vignette, 0.3, {alpha: 0}, 0);
            timeline.to(this.screen.itemsContainer, 0.3, {alpha: 1}, 0);
        }
        timeline.call(() => this.buttonLight.interactive = true, undefined, undefined, 0.7);
    }
}
